// ############################         ENUMS         ############################

import Controller from "./core/controller";

export enum UserHomepageStateActions {
    none,

    resetState,
    changeViewMode,
    updateTitle,
    setAsLive,
    toggleControlPanelCollapsed,

    homepageLoaded,
    tabInfoLoaded,

    selectTab,
    selectComponent,
    selectContent,

    reorderTabs,
    createTab,
    openTab,
    updateTab,
    removeTab,

    reorderComponents,
    createComponent,
    openComponent,
    updateComponent,
    removeComponent,

    reorderContent,
    addContent,
    removeContent,
    updateContent,
}

export enum UserHomePageTypes {
    phone = 'phone',
    web = 'web',
}

export enum UserHomepageEventEmitter {
    controlPanel = 'controlPanel',
    view = 'view'
}

/**
 * Home view components' search query types. This enum is used to determine which search engine to be used when
 * searching for products.
 */
export enum UserHomepageComponentSearchTypes {
    basic = 'Basic',
    application = 'Application',
    elasticTechnical = "ElasticTechnical",
}

export enum UserHomepageComponentTypes {
    slideShow = 1 as const,
    category = 2 as const,
    largeProduct = 3 as const,
    mediumProduct = 4 as const,
    smallProduct = 5 as const,
    timeBased = 6 as const,
    banner = 7 as const,
    imageCarousel = 8 as const,
}

export enum UserHomepageProductTypes {
    small,
    medium,
    large,
}

export enum UserHomepageComponentExpandability {
    none,
    dates,
    content,
}

export enum UserHomepageControllerEvents {
    onTabSelected = 'onTabSelected',
    onViewModeChanged = 'onViewModeChanged',
    onComponentSelected = 'onComponentSelected',
    onContentSelected = 'onContentSelected',
    onComponentSearchSelected = 'onComponentSearchSelected',
    onContentClicked = 'onContentClicked',
    onProductTagSelected = 'onProductTagSelected',

    onComponentCreated = 'onComponentCreated',
    onUnloadedTabSelected = 'onUnloadedTabSelected',
}

export enum UserHomepageControlPanelEvents {
    onTabSelected = 'onTabSelectedByControlPanel',
    onComponentSelected = 'onComponentSelectedByControlPanel',
    onContentSelected = 'onContentSelectedByControlPanel',
    onComponentSearchSelected = 'onComponentSearchSelectedByControlPanel',
    onContentClicked = 'onContentClickedByControlPanel',
    onProductTagSelected = 'onProductTagSelectedByControlPanel',
}

export enum UserHomepageViewEvents {
    onTabSelected = 'onTabSelectedByView',
    onComponentSelected = 'onComponentSelectedByView',
    onContentSelected = 'onContentSelectedByView',
    onComponentSearchSelected = 'onComponentSearchSelectedByView',
    onContentClicked = 'onContentClickedByView',
    onProductTagSelected = 'onProductTagSelectedByView',
}

export enum UserHomepageViewMode {
    preview = 'preview-mode',
    edit = 'edit-mode',
}

// ############################         INTERFACES         ############################

export interface UserHomepageControllerQueuedEvent {
    id: string,
    event: string,
    args: any[],
}

export interface UserHomepageDispatcherAction {
    type: UserHomepageStateActions,
    payload: any,
}

export interface UserHomepageControlPanel {
    collapsed: boolean,
}

export interface UserHomepageElement<T = number> {
    controlPanelElementId?: string,
    phoneViewElementId?: string,
    webViewElementId?: string,
    id?: T,
}

export interface UserHomepageSortable {
    orderIndex?: number,
}

export interface UserHomepageExpandable<T = boolean> {
    expanded?: T,
}

export interface UserHomePageStateData {
    name?: string,
    tabs?: Array<UserHomepageTab>,
    isLive?: boolean,
    canEdit?: boolean,
}

export interface UserHomepageTab extends UserHomepageElement, UserHomepageExpandable {
    name?: string,
    orderIndex?: number,
    components?: Array<UserHomepageComponent>,
    loading: boolean,
}

export interface UserHomepageComponent extends UserHomepageElement, UserHomepageSortable, UserHomepageExpandable<UserHomepageComponentExpandability> {
    title?: string,
    searchType?: UserHomepageComponentSearchTypes,
    searchQuery?: {
        [p: string]: any,
    },
    type?: {
        id: UserHomepageComponentTypes,
    },
    startDate?: string,
    endDate?: string,
    industryId?: number,
    content?: UserHomepageComponentContent,
}

export interface UserHomepageComponentContent {
    finishDateTime?: string,
    imageWidth?: number | null,
    imageHeight?: number | null,
    banners?: Array<UserHomepageContent>,
    images?: Array<UserHomepageContent>,
    categories?: Array<UserHomepageContent>,
    parts?: Array<UserHomepageContent>,
}

export interface UserHomepageContent extends UserHomepageElement, UserHomepageSortable {
    [p: string]: any,
}

export interface UserHomepageSelectionState {
    tabId?: number,
    componentId?: number,
    contentId?: number,
}

export interface UserHomepageState extends UserHomepageElement {
    currentData?: UserHomePageStateData,
    updatedData?: UserHomePageStateData,
    selection: UserHomepageSelectionState,
    viewMode: UserHomepageViewMode,
    controlPanel: UserHomepageControlPanel,
    initialState?: any,
}

export interface InitialUserHomepageState {
    data?: Partial<UserHomePageStateData>,
    viewMode: UserHomepageViewMode,
    controlPanelCollapsed: boolean,
}

export interface UserPhoneHomepageColorTheme {
    red: string,
    redLogo: string,
    blue: string,
    violet: string,
    green: string,
    cardColor: string,
    pinkCardColor: string,
    black: string,
    white: string,
    compareCard: string,
    graySix: string,
    grayFour: string,
    grayTwo: string,
    creditCardColor1: string,
    creditCardColor2: string,
    giftCardColor1: string,
    giftCardColor2: string,
    chatLight: string,
    chatDark: string,
}

export interface UserWebHomepageColorTheme {
    newDarkBlue: string,
    newBlueColor: string,
    productHeaderBlue: string,
    lighterBlue: string,
    transparentBlue: string,
    showMoreBackground: string,
    whiteColor: string,
    loadingBackground: string,
    mainPageHeaderRed: string,
    primaryColor: string,
    primaryColor2: string,
    primaryColor3: string,
    primaryColor4: string,
    primaryColor5: string,
    primaryColor6: string,
    secondaryColor: string,
    secondaryColor2: string,
    secondaryColor3: string,
    secondaryColor4: string,
    secondaryColor5: string,
    grey1: string,
    grey2: string,
    grey3: string,
    grey4: string,
    grey5: string,
    grey6: string,
    primaryTextColor: string,
    secondaryTextColor: string,
    disabledTextColor: string,
    hintTextColor: string,
    black: string,
    productInformationBoxShadow: string,
    largeProductBodyBorderColor: string,
    tagsContainerBackgroundColor: string,
}

export interface UserHomepageProductContext {
    enforcedHeight: number,
}

export interface UserHomepageViewContext {
    zoom: number,
    viewportWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'auto';
}

export interface UserHomepageControlPanelContext {
    technicalProperties: Array<any>,
    categories: Array<any>,
    applicationData: {
        makes: Array<any>,
        models: Array<any>,
    },
}

// ############################         DEFAULT VALUES         ############################

export const DefaultUserHomepageSelectionState: UserHomepageSelectionState = {
    tabId: undefined,
    componentId: undefined,
    contentId: undefined,
}

export const DefaultUserHomepageStateContextValue: UserHomepageState = {
    id: undefined,
    controlPanelElementId: undefined,
    webViewElementId: undefined,
    phoneViewElementId: undefined,
    currentData: undefined,
    updatedData: undefined,
    selection: DefaultUserHomepageSelectionState,
    viewMode: UserHomepageViewMode.preview,
    controlPanel: {
        collapsed: false,
    },
    initialState: null,
};

export const DefaultUserHomepageControllerContextValue = new Controller(() => (void 0));

export const DefaultUserHomepageProductContextValue: UserHomepageProductContext = {
    enforcedHeight: 0,
}

export const DefaultUserHomepageViewContextValue: UserHomepageViewContext = {
    zoom: 1,
    viewportWidth: 'lg',
}

/** Class-based layout breakpoint for web homepage preview (grid/slider density). Does not affect the device simulator shell. */
export const UserHomepageWebPreviewViewportWidth: UserHomepageViewContext['viewportWidth'] = 'lg';

export const DefaultPhoneUserHomepageColorThemeContextValue: UserPhoneHomepageColorTheme = {
    red: '#FF0000',
    blue: '#0062AB',
    redLogo: '#E42C5E',
    violet: '#9D6598',
    green: '#458268',
    cardColor: '#F2F7FB',
    pinkCardColor: '#FEF4F7',
    black: '#000000',
    white: '#FFFFFF',
    compareCard: '#F8F8F8',
    graySix: '#F2F2F2',
    grayFour: '#BDBDBD',
    grayTwo: '#4F4F4F',
    creditCardColor1: '#E42C5E',
    creditCardColor2: '#0062AB',
    giftCardColor1: '#E3305E',
    giftCardColor2: '#8E4A88',
    chatLight: '#B45E8D',
    chatDark: `#6F6CA4`,
}


export const DefaultWebUserHomepageColorThemeContextValue: UserWebHomepageColorTheme = {
    newDarkBlue: '#002e55',
    newBlueColor: '#003563',
    productHeaderBlue: '#003b6f',
    lighterBlue: '#f2f7fb',
    transparentBlue: '#005aab',
    showMoreBackground: '#F5F7FA',
    whiteColor: '#FFFFFF',
    loadingBackground: '#F1F2F5',
    mainPageHeaderRed: '#FDEEF2',
    primaryColor: '#e42c5e',
    primaryColor2: '#ec6286',
    primaryColor3: '#f296af',
    primaryColor4: '#f8cad7',
    primaryColor5: '#f8cad7',
    primaryColor6: '#fce8ee',
    secondaryColor: '#005aab',
    secondaryColor2: '#4183c0',
    secondaryColor3: '#80add6',
    secondaryColor4: '#FFFFFF',
    secondaryColor5: '#80add6', // WITH 0.09 OPACITY
    grey1: '#F2F2F2',
    grey2: '#746862',
    grey3: '#B4AEAD',
    grey4: '#a0a0a0',
    grey5: '#f1f4f8',
    grey6: '#414042', // WITH 0.08 opacity,
    primaryTextColor: '#404042',
    secondaryTextColor: '#858585',
    disabledTextColor: '#000000', // with 0.4 opacity
    hintTextColor: '#000000', // with 0.54 opacity
    black: '#000000',
    productInformationBoxShadow: '#00000003',
    largeProductBodyBorderColor: '#005baa', // with opacity 0.15
    tagsContainerBackgroundColor: '#d1d5e1', // with opacity 0.15


}


// ############################         CONSTANTS         ############################

